import {apiClient} from '@common/http/query-client';
import {useEffect} from 'react';
import {useAuth} from './use-auth';

// how often to proactively verify the session while the app stays open
const CHECK_INTERVAL = 5 * 60 * 1000;
// don't fire more than once per this window (focus + visibility can overlap)
const MIN_GAP = 30 * 1000;

let lastCheck = 0;

function pingSession() {
  const now = Date.now();
  if (now - lastCheck < MIN_GAP) return;
  lastCheck = now;
  // A 401/419 here is caught by the global response interceptor, which redirects
  // to the login page. Other errors (offline, 5xx) are ignored on purpose — we
  // only care about an expired session, not transient network issues.
  apiClient.get('session/alive').catch(() => {});
}

// Proactively detects a session that was destroyed server-side while the tab was
// idle: checks when the user returns to the tab (focus / becomes visible) and on
// a slow background interval, so the user is sent to login without first having
// to click something that silently fails. Only active while logged in.
export function SessionActivityWatcher() {
  const {isLoggedIn} = useAuth();

  useEffect(() => {
    if (!isLoggedIn) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') pingSession();
    };
    const interval = window.setInterval(pingSession, CHECK_INTERVAL);
    window.addEventListener('focus', pingSession);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', pingSession);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoggedIn]);

  return null;
}
